<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="progress-diagram-title" :field="fields?.title" tag="h2" />
      <JssRichText class="progress-diagram-desc" :field="fields?.subtitle" />
      <div class="icon-listdata" v-if="fields?.iconList?.length > 0">
        <div v-for="(item, index) in fields?.iconList" :key="index" class="icon-item">
          <AdaptiveImage class="icon-image" :field="item?.fields?.image" />
          <JssRichText class="icon-text" :field="item?.fields?.text" />
        </div>
      </div>
      <div ref="cardListRef" class="card-list" v-if="fields?.cardList?.length > 0">
        <div v-for="(item, index) in fields?.cardList" :key="index" class="card-item">
          <AdaptiveImage class="card-image" :field="item?.fields?.image" />
          <JssRichText class="card-title" :field="item?.fields?.title" tag="h3" />
          <JssRichText class="card-desc" :field="item?.fields?.desc" />
        </div>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import utils from '@/utils';

export default {
  name: 'ProgressDiagram',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'progress-diagram'));

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    const cardListRef = ref(null);

    const equalizeTitleHeights = () => {
      if (!cardListRef.value) return;
      const titles = cardListRef.value.querySelectorAll('.card-title');
      if (!titles.length) return;

      titles.forEach((el) => (el.style.minHeight = '0'));
      // force reflow to get accurate scrollHeight
      document.body.offsetHeight;

      const maxHeight = Math.max(...Array.from(titles).map((el) => el.scrollHeight));
      titles.forEach((el) => (el.style.minHeight = `${maxHeight}px`));
    };

    let resizeHandler = null;

    onMounted(() => {
      nextTick(equalizeTitleHeights);
      // eslint-disable-next-line vue/valid-next-tick
      resizeHandler = () => nextTick(equalizeTitleHeights);
      window.addEventListener('resize', resizeHandler);
    });

    onBeforeUnmount(() => {
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
    });

    watch(
      () => props.fields,
      () => {
        nextTick(equalizeTitleHeights);
      },
      { deep: true }
    );

    return {
      rootClass,
      rootStyle,
      cardListRef,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.progress-diagram {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    text-align: center;
  }

  &-desc {
    font-size: 14px;
    line-height: 1.6;
    color: $black;
    margin-top: 8px;
    text-align: center;
  }

  .icon-listdata {
    display: flex;
    flex-direction: row;
    gap: 24px;
    margin-top: 24px;
    overflow-x: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: 0 20px;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 26px;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, #d4d4d4, #a8a8a8);
      z-index: -1;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .icon-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    .icon-image {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 48px;
        height: 48px;
        object-fit: contain;
      }
    }

    .icon-text {
      font-size: 14px;
      font-weight: 500;
      color: $blue-night;
    }
  }

  .card-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-top: 32px;
  }

  .card-item {
    background: #fafafa;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .card-image {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 64px;
        height: 64px;
        object-fit: contain;
      }
    }

    .card-title {
      font-size: 20px;
      font-weight: 600;
      color: #0b172a;
      margin-top: 8px;
    }

    .card-desc {
      font-size: 14px;
      line-height: 1.5;
      color: #787878;
      flex: 1;
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-desc {
      font-size: 18px;
    }

    .icon-listdata {
      flex-direction: row;
      justify-content: space-between;
      margin-top: 40px;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        top: 31px;
        height: 2px;
        background: linear-gradient(90deg, #d4d4d4, #a8a8a8);
        z-index: -1;
      }
    }

    .icon-item {
      display: flex;
      align-items: center;
      gap: 8px;

      .icon-image {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 64px;
          height: 64px;
          object-fit: contain;
        }
      }

      .icon-text {
        font-size: 16px;
        font-weight: 500;
        color: $blue-night;
        text-align: center;
      }
    }
    .card-list {
      flex-direction: row;
      gap: 30px;
      margin-top: 40px;
    }

    .card-item {
      flex: 1;
      background: #fafafa;
      border-radius: 16px;
      padding: 32px;
      display: flex;
      flex-direction: column;
      gap: 16px;

      .card-image {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 80px;
          height: 80px;
          object-fit: contain;
        }
      }

      .card-title {
        font-size: 24px;
        font-weight: 600;
        color: #0b172a;
        margin-top: 14px;
      }

      .card-desc {
        font-size: 16px;
        line-height: 1.6;
        color: #787878;
        flex: 1;
      }
    }
  }
}
</style>
